import { useState, useEffect, useRef } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import { Bot, X, Send, Loader2, Plus, History, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Routes where the floating bot should NOT appear
const HIDE_ON = [
  "/login",
  "/register",
  "/auth",
  "/onboarding",
  "/forgot-password",
  "/reset-password",
];

export default function AIxTribeBotFAB() {
  const { user, getAuthHeaders } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (open && user) fetchSessions();
  }, [open, user]);

  // Don't render if user not logged in or on excluded routes
  if (!user) return null;
  if (HIDE_ON.some(p => location.pathname.startsWith(p))) return null;

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/workspace/bot/sessions`, { headers: getAuthHeaders() });
      setSessions(res.data || []);
    } catch (e) { /* silent */ }
  };

  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/workspace/bot/${sid}`, { headers: getAuthHeaders() });
      setSessionId(sid);
      setMessages(res.data.messages || []);
      setShowSessions(false);
    } catch { /* silent */ }
  };

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/workspace/bot`,
        { message: msg, session_id: sessionId },
        { headers: getAuthHeaders() }
      );
      setMessages(prev => [...prev, { role: "assistant", content: res.data.reply }]);
      if (!sessionId) setSessionId(res.data.session_id);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Something went wrong. Please try again!" }]);
    } finally {
      setLoading(false);
    }
  };

  const newChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowSessions(false);
  };

  const quickPrompts = [
    "How do I create a Company page?",
    "Help me write a LinkedIn-style post about AI",
    "What tools are available on AIxTribe?",
    "How does job posting work & pricing?",
  ];

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open AIxTribe Bot"
          data-testid="aixtribe-bot-fab"
          className="fixed z-[60] right-4 bottom-24 sm:bottom-6 w-40 h-40 rounded-full bg-transparent flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
        >
          <img
            src="/assets/aixtribe-bot.png"
            alt="AIxTribe Bot"
            className="w-full h-full object-contain drop-shadow-[0_0_12px_rgba(192,132,252,0.55)]"
            draggable={false}
          />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          className="fixed z-[60] right-3 left-3 bottom-3 sm:right-5 sm:left-auto sm:bottom-5 sm:w-[400px] h-[78vh] sm:h-[600px] bg-[#0A0A0F] border border-[#1A1A24] rounded-2xl shadow-2xl shadow-rose-500/10 flex flex-col overflow-hidden"
          data-testid="aixtribe-bot-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1A1A24] bg-gradient-to-r from-rose-500/10 to-orange-500/10">
            <div className="flex items-center gap-3 min-w-0">
              {showSessions ? (
                <button onClick={() => setShowSessions(false)} className="text-gray-400 hover:text-white" data-testid="bot-back-from-history">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              ) : (
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <img src="/assets/aixtribe-bot.png" alt="AIxTribe Bot" className="w-full h-full object-contain" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-white font-bold text-sm truncate">AIxTribe Bot</p>
                <p className="text-[10px] text-gray-500 truncate">Your personal AI guide</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setShowSessions(s => !s)} title="History" className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white" data-testid="bot-history-toggle">
                <History className="w-4 h-4" />
              </button>
              <button onClick={newChat} title="New chat" className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white" data-testid="bot-new-chat">
                <Plus className="w-4 h-4" />
              </button>
              <button onClick={() => setOpen(false)} title="Close" className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white" data-testid="bot-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          {showSessions ? (
            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="bot-sessions-list">
              {sessions.length === 0 ? (
                <p className="text-center text-gray-600 text-sm py-8">No conversations yet</p>
              ) : sessions.map(s => (
                <button key={s.session_id} onClick={() => loadSession(s.session_id)}
                  className="w-full text-left p-3 rounded-lg bg-[#15151F] border border-[#1A1A24] hover:border-rose-500/30 transition-colors"
                  data-testid={`bot-session-${s.session_id}`}>
                  <p className="text-white text-xs truncate">{s.preview || "New conversation"}</p>
                  <p className="text-gray-600 text-[10px] mt-1">{s.msg_count} messages</p>
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="bot-messages">
                {messages.length === 0 && (
                  <div className="flex flex-col items-center pt-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-500/20 to-orange-500/20 flex items-center justify-center mb-3">
                      <Bot className="w-8 h-8 text-rose-400" />
                    </div>
                    <h3 className="text-white font-bold text-base mb-1">Hi {user?.name?.split(" ")[0] || "there"}!</h3>
                    <p className="text-gray-500 text-xs text-center mb-4">Ask me anything about AIxTribe.</p>
                    <div className="grid grid-cols-1 gap-2 w-full">
                      {quickPrompts.map((q, i) => (
                        <button key={i} onClick={() => setInput(q)}
                          className="text-left px-3 py-2 rounded-lg bg-[#15151F] border border-[#1A1A24] text-gray-400 text-xs hover:border-rose-500/30 hover:text-rose-300 transition-all"
                          data-testid={`bot-quick-${i}`}>
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((msg, i) => (
                  <div key={i} className={cn("flex", msg.role === "user" ? "justify-end" : "justify-start")}>
                    {msg.role === "assistant" && (
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500/20 to-orange-500/20 flex items-center justify-center mr-2 mt-1 flex-shrink-0">
                        <Bot className="w-4 h-4 text-rose-400" />
                      </div>
                    )}
                    <div className={cn(
                      "max-w-[80%] rounded-2xl px-3 py-2 text-xs leading-relaxed",
                      msg.role === "user"
                        ? "bg-gradient-to-r from-rose-500/20 to-orange-500/20 border border-rose-500/20 text-white"
                        : "bg-[#15151F] border border-[#1A1A24] text-gray-200"
                    )}>
                      {msg.role === "assistant" ? (
                        <div className="prose prose-invert prose-xs max-w-none" dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            msg.content
                              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                              .replace(/`(.*?)`/g, '<code class="bg-[#0A0A0F] px-1 py-0.5 rounded text-rose-300 text-[10px]">$1</code>')
                              .replace(/\n/g, '<br/>')
                          )
                        }} />
                      ) : (
                        <p>{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex items-start gap-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-rose-400" />
                    </div>
                    <div className="bg-[#15151F] border border-[#1A1A24] rounded-2xl px-3 py-2.5">
                      <div className="flex gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                        <div className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                        <div className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSend} className="flex gap-2 p-3 border-t border-[#1A1A24] bg-[#0A0A0F]" data-testid="bot-input-form">
                <input
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Ask AIxTribe Bot anything..."
                  className="flex-1 bg-[#15151F] border border-[#2A2A3A] rounded-full px-4 py-2 text-white text-xs placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-rose-500/50"
                  data-testid="bot-input"
                />
                <Button type="submit" disabled={loading || !input.trim()} className="bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-full h-9 w-9 p-0 flex-shrink-0" data-testid="bot-send">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
